import qs from 'query-string'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useApolloClient } from '@apollo/react-hooks'
import { path } from 'ramda'

import { planType } from '../../shared/subscription-constants'
import { usePlanStaticDetails } from '../../../hooks/usePlanStaticDetails'
import { getGQLErrorCode } from '../../../lib/apollo'

import ACCOUNT_QUERY_WITH_DIR from '../../../../graphql/queries/account-with-directives.gql'
import SUBSCRIPTION_CONFIRMATION_VIEW_QUERY from '../../../../graphql/queries/subscription-confirmation-view.gql'

/**
 * Responsible for fetching and providing data to the
 * subscription confirmation screen during sign up flow
 */
export const useSubscriptionConfirmation = () => {
  const [loading, setLoading] = useState(true)
  const [details, setDetails] = useState([])
  const [accountData, setAccountData] = useState(null)

  const history = useHistory()
  const { search } = useLocation()
  const client = useApolloClient()

  const {
    voucher,
    orderId,
    plan
  } = qs.parse(search)
  const isTvodPlan = plan === planType.TVOD
  const planStaticDetails = usePlanStaticDetails(plan)

  // API functions integrated directly
  const refreshUserAccount = async () => {
    try {
      const account = await client.query({
        query: ACCOUNT_QUERY_WITH_DIR,
        fetchPolicy: 'network-only',
        variables: {
          withSubscription: true,
          withClearCachedSubscriptions: true
        }
      })
      return account
    } catch (error) {
      const errorCode = getGQLErrorCode(error)
      throw new Error(errorCode)
    }
  }

  const getSubscriptionConfirmationDetails = async () => {
    try {
      const response = await client.query({
        query: SUBSCRIPTION_CONFIRMATION_VIEW_QUERY,
        variables: {
          orderId,
          paymentMethodId: ''
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
      })

      const subConfirmationDetails = path(
        ['data', 'subscriptionConfirmationView', 'details'],
        response
      )

      return subConfirmationDetails
    } catch (error) {
      const errorCode = getGQLErrorCode(error)
      throw new Error(errorCode)
    }
  }

  useEffect(() => {
    const getServerData = async () => {
      try {
        const accountQueryData = await refreshUserAccount()
        setAccountData(accountQueryData.data)
        if (isTvodPlan) {
          setLoading(false)
          setDetails('')
          return
        }
        const subConfirmationDetails = await getSubscriptionConfirmationDetails(orderId)
        const planDetails = [planStaticDetails, ...subConfirmationDetails]

        setDetails(planDetails)

        setLoading(false)
      } catch (error) {
        // Redirect to homepage
        history.push('/')
      }
    }

    getServerData()
  }, [orderId, plan, planStaticDetails])

  return {
    plan,
    loading,
    details,
    accountData,
    orderId,
    voucher,
    hasSubscription: Boolean(orderId),
    isTvodPlan
  }
}
